import { type FC, memo, useMemo } from 'react'

import type { PositionSide } from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import Currency from 'components/Currency'
import { Body } from 'components/Text'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import type { SLTPInputType } from '../Trade/SLTPInputField'

type StopLossAndTakeProfitSummary = {
	type: SLTPInputType
	value: string
	sizePercentage: string
	entryPrice: string
	leverage: string
	enterNotionalSize: string
	positionSide: PositionSide
}

const StopLossAndTakeProfitSummary: FC<StopLossAndTakeProfitSummary> = memo(
	({ type, entryPrice, positionSide, leverage, enterNotionalSize, sizePercentage, value }) => {
		const isStopLoss = type === 'stop-loss'
		const calculatePL = useMemo(() => {
			if (!value || !entryPrice || !positionSide || !enterNotionalSize) return ''
			const priceWei = wei(value)
			const entryPriceWei = wei(entryPrice)
			const leverageWei = wei(leverage)

			const diff =
				positionSide === 'short'
					? isStopLoss
						? priceWei.sub(entryPriceWei)
						: entryPriceWei.sub(priceWei)
					: isStopLoss
						? entryPriceWei.sub(priceWei)
						: priceWei.sub(entryPriceWei)

			const percentage = entryPriceWei.gt(0) ? diff.div(entryPriceWei).mul(leverage) : wei(0)
			const profitLoss = leverageWei.gt(0)
				? wei(enterNotionalSize)
						.mul(percentage.div(leverage))
						.mul(isStopLoss ? -1 : 1)
				: wei(0)

			return profitLoss.mul(sizePercentage)
		}, [entryPrice, isStopLoss, positionSide, leverage, value, sizePercentage, enterNotionalSize])

		return (
			<Container>
				<Trans
					i18nKey="futures.market.trade.edit-sl-tp.price-input-msg"
					components={[<Body key="pnl-msg-0" inline color="primary" />, <PnL key="pnl-msg-1" />]}
					values={{
						price: value || '--',
						pnl: calculatePL.toString(),
						orderType: type,
					}}
				/>
			</Container>
		)
	}
)

interface PnlProps {
	children?: string[]
}

const PnL: FC<PnlProps> = memo(({ children }) => {
	return children?.[0] ? <Currency.Price colored price={children?.[0]} /> : '$--'
})

const Container = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	margin-top: 5px;
	margin-bottom: 10px;
`

export default StopLossAndTakeProfitSummary
